import { useApolloClient, useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { firstUserErrorCode } from '@/graphql/errors';
import { SIGN_IN_WITH_LINK } from '@/graphql/mutations/auth';
import type { SignInLinkData } from '../types';

export type SignInWithLinkOutcome =
  | { status: 'signed-in'; hasWorkspace: boolean }
  | { status: 'expired'; link: SignInLinkData }
  | { status: 'invalid' };

type UseSignInWithLinkResult = {
  signInWithLink: (token: string) => Promise<SignInWithLinkOutcome>;
  isSigningIn: boolean;
};

export function useSignInWithLink(): UseSignInWithLinkResult {
  const client = useApolloClient();
  const [mutate, { loading }] = useMutation(SIGN_IN_WITH_LINK, {
    context: { skipGlobalErrorToast: true },
  });

  const signInWithLink = useCallback(
    async (token: string): Promise<SignInWithLinkOutcome> => {
      const { data } = await mutate({ variables: { input: { token } } });
      const payload = data?.signInWithLink;
      if (payload?.viewer) {
        // A new session: everything cached before belonged to nobody.
        await client.resetStore();
        return { status: 'signed-in', hasWorkspace: payload.viewer.workspace !== null };
      }
      const code = firstUserErrorCode(payload?.userErrors);
      if ((code === 'LINK_EXPIRED' || code === 'LINK_USED') && payload?.expiredLink) {
        return { status: 'expired', link: payload.expiredLink };
      }
      return { status: 'invalid' };
    },
    [client, mutate],
  );

  return { signInWithLink, isSigningIn: loading };
}
